'use client';

// @boty/ui - BotyLogo component
// Combines BotyIcon with "Boty" text for branding

import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { BotyIcon } from './BotyIcon';

export type BotyLogoSize = 'small' | 'medium' | 'large';

export interface BotyLogoProps {
  /** Logo size preset: 'small' (24px icon), 'medium' (32px icon), 'large' (48px icon) */
  size?: BotyLogoSize;
  /** Additional CSS class name */
  className?: string;
}

const sizeConfig = {
  small: { icon: 24, textVariant: 'body1' as const },
  medium: { icon: 32, textVariant: 'h6' as const },
  large: { icon: 48, textVariant: 'h4' as const },
};

/**
 * BotyLogo - Boty mascot icon with "Boty" text
 *
 * Composite component for branding that shows the BotyIcon with the product name.
 * Supports RTL layouts via CSS logical properties.
 *
 * @param size - 'small' (header), 'medium' (default), 'large' (landing pages)
 * @param className - Additional CSS class name
 *
 * @example
 * ```tsx
 * // Default medium size
 * <BotyLogo />
 *
 * // Small for headers
 * <BotyLogo size="small" />
 *
 * // Large for landing pages
 * <BotyLogo size="large" />
 * ```
 */
export function BotyLogo({ size = 'medium', className }: BotyLogoProps): ReactNode {
  const config = sizeConfig[size];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1, // 8px gap using CSS logical properties
      }}
      className={className}
    >
      <BotyIcon size={config.icon} />
      <Typography
        variant={config.textVariant}
        sx={{
          fontWeight: 600,
          color: 'primary.main',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        Boty
      </Typography>
    </Box>
  );
}

BotyLogo.displayName = 'BotyLogo';
